/** @format */

import { useState } from 'react';

import { Formik } from 'formik';
import { PatternFormat } from 'react-number-format';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Logo from '../../Assets/Images/logo.jpg';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';

const validationSchema = Yup.object().shape({
  mobile: Yup.string()
    .required('لطفا موبایل را وارد نمایید')
    .min(11, 'موبایل نمیتواند کمتر از 11 کاراکتر باشد')
    .max(11, 'موبایل نمیتواند بیشتر از 11 کاراکتر باشد'),
});

const validationSchemaVerify = Yup.object().shape({
  code: Yup.string()
    .required('لطفا کد تایید را وارد نمایید')
    .min(5, 'کد تایید نمیتواند کمتر از 5 کاراکتر باشد')
    .max(9, 'کد تایید نمیتواند بیشتر از 5 کاراکتر باشد'),
  password: Yup.string()
    .required('کلمه عبور نمی تواند خالی باشد')
    .min(8, 'کلمه عبور نمیتواند کمتر از8 کاراکتر باشد')
    .max(64, 'کلمه عبور نباید بیشتر از 64 کاراکتر باشد'),
  password_confirmation: Yup.string().oneOf(
    [Yup.ref('password'), null],
    'کلمه عبور و تکرار آن باید یکسان باشد.',
  ),
});

const ForgetPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showActivationForm, setShowActivationForm] = useState(false);
  const [mobile, setMobile] = useState('');

  const handleApiError = (err) => {
    if (
      typeof err === 'object' &&
      !Array.isArray(err) &&
      err !== null &&
      err?.status === 422
    ) {
      Object.values(err?.data?.errors || {})?.forEach((item) => {
        error(item[0]);
      });
      return;
    }

    const message =
      err?.data?.message ||
      (typeof err?.data === 'string' ? err.data : null) ||
      'خطایی در انجام عملیات پیش آمده است.';
    error(message);
  };

  const onSubmitRegisterForm = async (values, { setSubmitting }) => {
    setMobile(values.mobile);
    setLoading(true);

    await useApi.post(
      '/forget-password',
      values,
      () => {
        success('کد تایید با موفقیت ارسال شد.');
        setShowActivationForm(true);
      },
      handleApiError,
    );

    setSubmitting(false);
    setLoading(false);
  };

  const onSubmitVerifyForm = async (values, { setSubmitting }) => {
    setLoading(true);

    const payload = {
      mobile,
      code: values.code.replaceAll(' ', ''),
      password: values.password,
      password_confirmation: values.password_confirmation,
    };

    await useApi.post(
      '/reset-password',
      payload,
      () => {
        success('کلمه عبور با موفقیت تغییر کرد.');
        navigate('/login');
      },
      handleApiError,
    );

    setSubmitting(false);
    setLoading(false);
  };

  if (showActivationForm) {
    return (
      <div className='w-100 d-flex flex-column justify-content-center align-items-center vh-100 login-page'>
        <section className='container'>
          <div className='shadow-lg rounded-2 bg-white overflow-y-auto p-2'>
            <Formik
              key='forget-password-verify'
              initialValues={{
                code: '',
                password: '',
                password_confirmation: '',
              }}
              onSubmit={onSubmitVerifyForm}
              validationSchema={validationSchemaVerify}
            >
              {({
                values,
                errors,
                handleChange,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className='row'>
                    <div className='col-12'>
                      <div className='d-flex flex-column p-2'>
                        <div className='w-100 d-flex flex-column justify-content-center align-items-center'>
                          <img className='w-15' src={Logo} alt='logo' />
                        </div>
                        <div className='d-flex flex-column justify-content-center text-center my-3'>
                          <h3 className='text-dark f-14'>بازیابی کلمه عبور</h3>
                          <p className='text-muted f-13 mb-0'>
                            کد ارسال‌شده به {mobile} را وارد کنید
                          </p>
                        </div>
                        <div className='d-flex flex-row my-3'>
                          <hr className='w-30 bg-hr' />
                          <span className='text-dark f-14 align-self-center'>
                            کد تایید و رمز جدید
                          </span>
                          <hr className='w-30 bg-hr' />
                        </div>
                      </div>
                    </div>

                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left d-flex flex-row justify-content-start mx-1'>
                            کد تایید
                          </label>
                          <PatternFormat
                            format='# # # # #'
                            className={`form-control bg-none border-0 text-center rounded-1 m-0 dir-ltr p-0 ${
                              errors.code ? 'invalid-input' : ''
                            }`}
                            mask='_'
                            name='code'
                            value={values.code}
                            onValueChange={(formatted) => {
                              setFieldValue('code', formatted.formattedValue);
                            }}
                            autoComplete='one-time-code'
                          />
                          <ErrorMessage error={errors.code} classes='my-2 f-13' />
                        </div>
                        <div
                          className='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{ position: 'absolute', top: '-16px', left: '0px' }}
                        >
                          <i className='far fa-mobile f-18'></i>
                        </div>
                      </div>
                    </div>

                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left d-flex flex-row justify-content-start mx-1'>
                            کلمه عبور جدید
                          </label>
                          <input
                            type='password'
                            value={values.password}
                            name='password'
                            onChange={handleChange}
                            className='form-control bg-none border-0 rounded-1 m-0 p-0'
                            autoComplete='new-password'
                          />
                          <ErrorMessage
                            error={errors.password}
                            classes='my-2 f-13'
                          />
                        </div>
                        <div
                          className='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{ position: 'absolute', top: '-16px', left: '0px' }}
                        >
                          <i className='far fa-lock f-18'></i>
                        </div>
                      </div>
                    </div>

                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3'>
                      <div
                        className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                        style={{ height: '40px' }}
                      >
                        <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                          <label className='f-13 text-left d-flex flex-row justify-content-start mx-1'>
                            تکرار کلمه عبور
                          </label>
                          <input
                            type='password'
                            value={values.password_confirmation}
                            name='password_confirmation'
                            onChange={handleChange}
                            className='form-control bg-none border-0 rounded-1 m-0 p-0'
                            autoComplete='new-password'
                          />
                          <ErrorMessage
                            error={errors.password_confirmation}
                            classes='my-2 f-13'
                          />
                        </div>
                        <div
                          className='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                          style={{ position: 'absolute', top: '-16px', left: '0px' }}
                        >
                          <i className='far fa-lock f-18'></i>
                        </div>
                      </div>
                    </div>

                    <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3'>
                      <div className='form-group d-flex flex-column'>
                        <button
                          className='btn bg-blue-sky-fill border rounded-1 w-100'
                          type='submit'
                          disabled={isSubmitting || loading}
                        >
                          {loading ? (
                            <div
                              className='spinner-border spinner-border-sm text-white mb-1'
                              role='status'
                            ></div>
                          ) : (
                            'تائید'
                          )}
                        </button>
                        <button
                          type='button'
                          className='btn btn-link text-danger f-13 mt-2'
                          onClick={() => {
                            setShowActivationForm(false);
                            setLoading(false);
                          }}
                        >
                          تغییر شماره موبایل
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className='w-100 d-flex flex-column justify-content-center align-items-center vh-100 login-page'>
      <section className='container'>
        <div className='shadow-lg rounded-2 bg-white overflow-y-auto p-2'>
          <Formik
            key='forget-password-mobile'
            initialValues={{ mobile: mobile || '' }}
            onSubmit={onSubmitRegisterForm}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              handleChange,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className='row'>
                  <div className='col-12'>
                    <div className='d-flex flex-column p-2'>
                      <div className='w-100 d-flex flex-column justify-content-center align-items-center'>
                        <img className='w-15' src={Logo} alt='logo' />
                      </div>
                      <div className='d-flex flex-column justify-content-center text-center my-3'>
                        <h3 className='text-dark f-14'>فراموشی کلمه عبور</h3>
                      </div>
                      <div className='d-flex flex-row my-3'>
                        <hr className='w-30 bg-hr' />
                        <span className='text-dark f-14 align-self-center'>
                          جهت بازیابی کلمه عبور فرم زیر را تکمیل کنید
                        </span>
                        <hr className='w-30 bg-hr' />
                      </div>
                    </div>
                  </div>

                  <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3'>
                    <div
                      className='w-100 d-flex flex-row align-items-center justify-content-center bg-blue-sky border rounded-1 my-4 position-relative'
                      style={{ height: '40px' }}
                    >
                      <div className='form-group w-90 h-100 p-0 m-0 form-group-pos'>
                        <label className='f-13 text-left d-flex flex-row justify-content-start mx-1'>
                          شماره همراه
                        </label>
                        <input
                          type='text'
                          value={values.mobile}
                          name='mobile'
                          onChange={handleChange}
                          className='form-control bg-none border-0 rounded-1 m-0 p-0'
                          disabled={loading || isSubmitting}
                        />
                        <ErrorMessage error={errors.mobile} classes='my-2 f-13' />
                      </div>
                      <div
                        className='d-flex flex-column align-items-center justify-content-center w-10 bg-blue-sky-dark rounded-1 h-100 mt-3 border'
                        style={{ position: 'absolute', top: '-16px', left: '0px' }}
                      >
                        <i className='far fa-mobile f-18'></i>
                      </div>
                    </div>
                  </div>

                  <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 offset-md-3'>
                    <div className='form-group d-flex flex-column'>
                      <button
                        className='btn bg-blue-sky-fill border rounded-1 w-100'
                        type='submit'
                        disabled={loading || isSubmitting}
                      >
                        {loading ? (
                          <div
                            className='spinner-border spinner-border-sm text-white mb-1'
                            role='status'
                          ></div>
                        ) : (
                          'تائید'
                        )}
                      </button>
                      <div className='text-center mt-2'>
                        <Link to='/login' className='text-danger f-13'>
                          بازگشت به صفحه ورود
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </section>
    </div>
  );
};

export default ForgetPassword;
